"use client";

import React from "react";
import { ArrowRight, CircleCheck, Loader, Shield } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const INTEREST_OPTIONS = ["Custom Software Development", "Cybersecurity Solutions", "AI & Automation", "Managed IT Services", "Web & Mobile Apps"];
function VisionaryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-primary/20 bg-card/30 p-8 text-center backdrop-blur-md">
        <div className="flex size-16 items-center justify-center rounded-lg bg-primary/20 text-primary">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">
          System Access Granted
        </h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/10">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Alex Rivera" className="flex h-10 w-full rounded-md border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="alex@apexitsolutions.au" className="flex h-10 w-full rounded-md border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company</FormLabel>
              <FormControl>
                <input type="text" placeholder="Enterprise Corp" className="flex h-10 w-full rounded-md border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Your Role</FormLabel>
              <FormControl>
                <input type="text" placeholder="IT Director" className="flex h-10 w-full rounded-md border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest_areas" render={({
      field
    }) => <FormItem className="rounded-md border border-border/30 bg-card/20 p-4 backdrop-blur-sm">
            <FormLabel className="text-base font-default font-medium">Service Interests</FormLabel>
            <FormDescription className="font-sans text-xs">
              Select the IT solutions you are interested in exploring.
            </FormDescription>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {INTEREST_OPTIONS.map((option, i) => <div data-index={i} key={option} className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <input type="checkbox" className="peer mt-1 size-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" checked={field.value?.includes(option)} onChange={e => {
              const current = field.value || [];
              const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
              field.onChange(updated);
            }} />
                  </FormControl>
                  <FormLabel className="font-sans text-sm text-muted-foreground peer-checked:text-foreground cursor-pointer transition-colors">
                    {option}
                  </FormLabel>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(163,230,53,0.4)] transition-all duration-300 font-default font-bold uppercase tracking-wider" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Syncing...
          </> : <>
            Initialize Partnership
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
      
      <p className="text-[10px] font-mono uppercase tracking-tight text-muted-foreground flex items-center justify-center sm:justify-start gap-1 mt-4">
        <Shield className="size-3 text-primary" />
        Encrypted Connection. View our <Link to="/ConnectPage" className="text-primary hover:underline">Privacy Protocol</Link>.
      </p>
    </Form>;
}
export default function SubscribeToTheFuture() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224566" id="subscribe-to-the-future" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 size-[500px] rounded-lg bg-secondary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 size-[600px] rounded-lg bg-primary/10 blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-mono font-medium text-primary uppercase tracking-widest">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75"></span>
                <span className="relative inline-flex size-2 rounded-lg bg-primary"></span>
              </span>
              Apex Intelligence Network
            </div>
            
            <h2 className="font-default text-4xl font-bold tracking-tighter sm:text-5xl lg:text-6xl text-foreground">
              Stay Ahead of the <span className="text-primary">Curve.</span>
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed font-sans">
              Secure your competitive edge with Apex IT Solutions. Receive exclusive technical insights, cybersecurity alerts, and AI automation strategies designed for the modern enterprise.
            </p>

            <ul className="space-y-4">
              {["Priority access to custom software roadmaps", "Critical cybersecurity threat intelligence", "Strategic AI & automation implementation guides"].map((item, i) => <li data-index={i} key={i} className="flex items-center gap-3 text-muted-foreground font-sans">
                  <CircleCheck className="size-5 text-primary shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-md border border-white/10 bg-card/40 p-6 sm:p-8 md:p-10 shadow-4xl backdrop-blur-xl relative overflow-hidden">
              {}
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 blur-3xl rounded-lg -translate-y-1/2 translate-x-1/2" />
              
              <div className="mb-8">
                <h3 className="font-default text-2xl font-semibold text-foreground tracking-tight">
                  Subscribe to the Future
                </h3>
                <p className="text-sm text-muted-foreground mt-2 font-sans">
                  Configure your technical preferences for tailored IT insights.
                </p>
              </div>

              <FormProvider formKey="subscribe_form_insights" formId="subscribe_form_insights" sectionName="Join the Visionaries" defaultValues={{
              full_name: "",
              email_address: "",
              company: "",
              role: "",
              interest_areas: []
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              }
            }} submitText="Initialize Partnership" successMessage="Connection established. Your first intelligence report is being routed to your inbox.">
                <VisionaryForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}